import { useState } from "react";
import { cn } from "../../lib/utils";

// Opacity of a tick by its distance from the active one (Figma "chemin de
// fer": 100, 72, 58, 44, 30, then 16 for everything further away).
const TICK_OPACITY = [1, 0.72, 0.58, 0.44, 0.3] as const;
const FARTHEST_TICK_OPACITY = 0.16;

// Tick lengths in px. A section's tick grows with its share of the page,
// between the shortest and the longest inactive tick; the active tick is
// always the long one.
const MIN_TICK = 6;
const MAX_TICK = 14;
const ACTIVE_TICK = 22;

export type SectionRailItem = {
  id: string;
  title: string;
  // The first words of the section, shown in the card a hovered tick opens.
  excerpt: string;
  // The section's height over the tallest section's, in (0, 1].
  weight: number;
};

function tickOpacity(distance: number): number {
  const opacity = TICK_OPACITY[distance];
  return opacity === undefined ? FARTHEST_TICK_OPACITY : opacity;
}

/**
 * The page's outline as a column of ticks, pinned to the right edge of a
 * long reading page (Figma "D3.explore.article", "chemin de fer"). One tick
 * per section, as long as the section is tall; the section being read is the
 * long dark tick and the others fade with their distance from it. Hovering a
 * tick shows the section's title and first words; clicking it scrolls there.
 *
 * A UI primitive: the hovered tick is machinery, so it owns that state. What
 * is active and how long each section is comes from the page.
 */
export function SectionRail({
  items,
  activeId,
  onSelect,
  label,
}: {
  items: SectionRailItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  // Accessible name of the rail, e.g. "Sections".
  label: string;
}): React.JSX.Element {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const activeIndex = items.findIndex((item) => item.id === activeId);

  return (
    <nav
      aria-label={label}
      className="flex items-center"
      onMouseLeave={() => {
        setHoveredId(null);
      }}
    >
      <ul className="m-0 flex list-none flex-col items-end p-0">
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          const isHovered = item.id === hoveredId;
          const width =
            isActive || isHovered
              ? ACTIVE_TICK
              : MIN_TICK + Math.round((MAX_TICK - MIN_TICK) * item.weight);
          return (
            <li key={item.id} className="relative flex justify-end">
              {/* The card hangs beside its own tick, so the first and last
                  sections open theirs at the top and the bottom of the rail.
                  It sits 10px left of the long tick: the 40px target less the
                  22px tick, pulled back by 8px. */}
              {isHovered ? (
                <div className="pointer-events-none absolute top-1/2 right-full -mr-2 flex w-80 -translate-y-1/2 flex-col gap-1 rounded-rail-card border border-ds-slate-100 bg-ds-white px-4 py-3.5 shadow-rail-card">
                  <span className="text-ui-body font-semibold text-ds-black">
                    {item.title}
                  </span>
                  <span className="line-clamp-3 text-footnote text-ds-slate-600">
                    {item.excerpt}
                  </span>
                </div>
              ) : null}
              <button
                type="button"
                aria-label={item.title}
                aria-current={isActive ? "location" : false}
                onMouseEnter={() => {
                  setHoveredId(item.id);
                }}
                onFocus={() => {
                  setHoveredId(item.id);
                }}
                onBlur={() => {
                  setHoveredId(null);
                }}
                onClick={() => {
                  onSelect(item.id);
                }}
                // The tick is a hairline; the button around it is the 40x10
                // target the pointer actually has to find, as the ticks are
                // drawn 10px apart. The buttons touch, so the pointer never
                // falls between two ticks.
                className="flex h-2.5 w-10 cursor-pointer items-center justify-end border-none bg-transparent p-0"
              >
                <span
                  className={cn(
                    "block rounded-xs bg-ds-black transition-all",
                    isActive || isHovered ? "h-0.5" : "h-rail-tick"
                  )}
                  style={{
                    width,
                    opacity:
                      isActive || isHovered
                        ? 1
                        : tickOpacity(
                            activeIndex === -1
                              ? index
                              : Math.abs(index - activeIndex)
                          ),
                  }}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
